import { cn } from "@newxsystems/shared";
import { StratosAction } from "../StratosAction/StratosAction";
import { StratosMedia } from "../StratosMedia/StratosMedia";
import { StratosRichText } from "../StratosRichText/StratosRichText";
import type { IHeroBackgroundImageProps } from "./HeroBackgroundImage.props";

const contentSpacing = {
  tight: "py-8 md:py-12",
  normal: "py-12 md:py-20",
  loose: "py-16 md:py-28",
} as const;

export const HeroBackgroundImage = ({
  alignment,
  buttons,
  description,
  eyebrow,
  heading,
  image,
  spacing,
}: IHeroBackgroundImageProps) => {
  const centered = alignment === "center";

  return (
    <div className="relative flex min-h-[42rem] overflow-hidden bg-night-flight md:min-h-[50rem]">
      <StratosMedia
        className="absolute inset-0 h-full rounded-none"
        imageClassName="animate-[stratos-hero-reveal_1.2s_cubic-bezier(0.16,1,0.3,1)_both] object-cover"
        media={image.image}
        priority
        sizes="100vw"
      />
      <div className="absolute inset-0 bg-night-flight/35" />
      <div
        className={cn(
          "stratos-container relative z-10 flex w-full items-end",
          contentSpacing[spacing || "normal"],
        )}
      >
        <div
          className={cn(
            "flex w-full max-w-[60rem] flex-col gap-6 text-cloud-white [text-shadow:0_2px_24px_rgb(16_23_34/0.7)]",
            centered && "mx-auto items-center text-center",
          )}
        >
          {eyebrow ? <p className="text-sm font-semibold text-sunlit">{eyebrow}</p> : null}
          {heading ? (
            <h1 className="max-w-[13ch] font-stratos-display text-5xl font-bold leading-[0.9] tracking-[-0.04em] text-balance [overflow-wrap:anywhere] md:text-7xl lg:text-8xl">
              {heading}
            </h1>
          ) : null}
          {description ? (
            <StratosRichText className="max-w-[60ch] text-cloud-white" content={description} />
          ) : null}
          {buttons?.length ? (
            <div className={cn("flex flex-wrap gap-3 pt-2", centered && "justify-center")}>
              {buttons.map((button, index) => (
                <StratosAction action={button} key={`${button.href}-${index}`} />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

HeroBackgroundImage.displayName = "Content::HeroBackgroundImage";
